import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { fmtDateTime } from "../../lib/time";
import api, { formatApiError } from "../../lib/api";
import { useClientProject } from "../../contexts/ClientProjectContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Download, RotateCcw, Filter, Boxes, CalendarDays, List } from "lucide-react";
import DayWiseStockMatrices from "../../components/charts/DayWiseStockMatrices";

const ALL = "__all";

function buildQS({ frm, to, projectId }) {
  const params = new URLSearchParams();
  if (frm) params.set("from", frm);
  if (to) params.set("to", to);
  if (projectId && projectId !== ALL) params.set("project_id", projectId);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export default function ClientStock() {
  const { projectId } = useClientProject();
  const [filters, setFilters] = useState({ frm: "", to: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("daywise"); // 'daywise' | 'entries'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQS({ ...filters, projectId });
      const r = await api.get(`/client/stocks${qs}`);
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => setFilters({ frm: "", to: "" });

  // Normalize for DayWiseStockMatrices: { created_at, store_name, items:[{sku,qty}] }
  const normalized = useMemo(
    () =>
      (rows || []).map((e) => ({
        created_at: e.created_at,
        store_name: e.store_name || "—",
        items: (e.quantities_named || []).map((q) => ({ sku: q.sku, qty: q.qty })),
      })),
    [rows],
  );

  const downloadEntriesExcel = () => {
    if (!rows.length) {
      toast.error("No stock data to export");
      return;
    }
    const aoa = [["Date", "Store", "SKU", "Qty"]];
    rows.forEach((e) => {
      const dt = e.created_at ? fmtDateTime(e.created_at) : "";
      (e.quantities_named || []).forEach((q) => {
        aoa.push([dt, e.store_name || "—", q.sku || "—", Number(q.qty || 0)]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 22 }, { wch: 24 }, { wch: 28 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock entries");
    const stamp = `${filters.frm || "all"}_${filters.to || "all"}`;
    XLSX.writeFile(wb, `stock-entries-${stamp}.xlsx`);
    toast.success("Excel downloaded");
  };

  return (
    <div className="space-y-6" data-testid="client-stock-page">
      {/* Filters */}
      <div className="glass-panel p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Filters
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">From</Label>
            <Input
              type="date"
              value={filters.frm}
              onChange={(e) => setFilters((f) => ({ ...f, frm: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="client-stock-filter-from"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">To</Label>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="client-stock-filter-to"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="rounded-xl w-full"
              onClick={reset}
              data-testid="client-stock-filter-reset"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          </div>
        </div>
        {loading && (
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Loading…
          </div>
        )}
      </div>

      {/* View toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex glass-panel">
          <button
            onClick={() => setView("daywise")}
            data-testid="client-stock-view-daywise"
            className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-2 ${
              view === "daywise" ? "bg-slate-900 text-white" : "bg-white text-neutral-600"
            }`}
          >
            <CalendarDays className="h-3.5 w-3.5" /> Day-wise matrix
          </button>
          <button
            onClick={() => setView("entries")}
            data-testid="client-stock-view-entries"
            className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-2 border-l border-slate-200/70 ${
              view === "entries" ? "bg-slate-900 text-white" : "bg-white text-neutral-600"
            }`}
          >
            <List className="h-3.5 w-3.5" /> Entries
          </button>
        </div>
        {view === "entries" && (
          <Button
            className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
            onClick={downloadEntriesExcel}
            data-testid="client-stock-entries-export"
          >
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
        )}
      </div>

      {view === "daywise" && (
        <DayWiseStockMatrices
          rows={normalized}
          filenamePrefix="client-stock-matrix"
          testidPrefix="client-stock"
        />
      )}

      {view === "entries" && (
        <div className="space-y-4">
          {rows.length === 0 && (
            <div className="glass-panel p-6 text-center text-neutral-500">
              No data
            </div>
          )}
          {rows.map((e) => (
            <div
              key={e.id}
              className="glass-panel p-4"
              data-testid={`client-stock-entry-${e.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    <Boxes className="h-4 w-4 text-neutral-500" />
                    {e.store_name || "—"}
                  </div>
                  <div className="text-xs text-neutral-500">
                    Total units:{" "}
                    <span className="font-mono font-semibold text-black">
                      {e.total_units ?? 0}
                    </span>
                  </div>
                </div>
                <div className="font-mono text-xs text-neutral-500">
                  {e.created_at && fmtDateTime(e.created_at)}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {(e.quantities_named || []).map((sd, idx) => (
                  <div
                    key={`${e.id}-${sd.sku}-${idx}`}
                    className="flex items-center justify-between px-3 py-2 bg-white/50 border border-slate-200/50"
                  >
                    <span className="text-sm">{sd.sku || "?"}</span>
                    <span className="font-mono font-semibold">{sd.qty}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
